import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';

import paths from '@src/paths';
import { toast } from '@src/components/core/toaster';
import defaultClient from '@src/api/clients/default';
import { useAuth } from '@src/context';

const STATUS_COLORS = {
  new_business: 'info',
  onboarding: 'warning',
  active_new: 'success',
  active: 'success',
  paused: 'default',
  off: 'error',
};

const STATUS_LABELS = {
  new_business: 'New',
  onboarding: 'Onboarding',
  active_new: 'Active (New)',
  active: 'Active',
  paused: 'Paused',
  off: 'Off',
};

const LD_LABELS = { not_configured: 'Not Set', awaiting_test: 'Awaiting Test', awaiting_confirm: 'Awaiting Confirm', confirmed: 'Confirmed' };
const LD_COLORS = { not_configured: 'default', awaiting_test: 'warning', awaiting_confirm: 'info', confirmed: 'success' };

const HEALTH_RANK = { stalled: 2, at_risk: 1, on_track: 0 };
const HEALTH_LABELS = { stalled: 'Stalled', at_risk: 'At Risk' };
const HEALTH_COLORS = { stalled: 'error', at_risk: 'warning' };

function renderVerticalsCaption(c) {
  if (!(c.verticals || []).length) return null;
  return (
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, display: 'block', mt: 0.25 }}>
      {c.verticals.map((v) => (v.lane ? `${v.name} — ${v.lane}` : v.name)).join(' · ')}
    </Typography>
  );
}

function renderFlowRows(c, navigate, indented) {
  return (c.vertical_flows || []).map((vf) => (
    <TableRow
      key={`${c.client_id}-vf-${vf.vertical_id}`}
      hover
      onClick={() => navigate(`${paths.onboarding.internal(c.client_slug)}?vertical=${vf.vertical_id}`)}
      sx={{ cursor: 'pointer', '& td': { py: 0.5, px: 1.5, fontSize: 12, borderBottom: '1px dashed', borderColor: 'grey.200' } }}
    >
      <TableCell sx={{ pl: indented ? 7 : 4 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography variant="caption" color="text.secondary">↳</Typography>
          <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600 }}>{vf.vertical_name}</Typography>
          <Chip label="New Vertical" size="small" color="secondary" variant="outlined" sx={{ fontSize: 10, height: 16 }} />
          {vf.lane && (
            <Chip label={vf.lane} size="small" variant="outlined" sx={{ fontSize: 10, height: 16 }} />
          )}
        </Stack>
      </TableCell>
      <TableCell>
        {vf.days_in != null && (
          <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>{vf.days_in}d</Typography>
        )}
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <LinearProgress variant="determinate" value={vf.progress}
            sx={{ flex: 1, height: 4, borderRadius: 3 }}
            color={vf.progress === 100 ? 'success' : 'secondary'} />
          <Typography variant="caption" sx={{ fontSize: 10 }}>{vf.complete}/{vf.total}</Typography>
        </Stack>
      </TableCell>
      <TableCell colSpan={6} />
    </TableRow>
  ));
}

function renderClientRow(c, navigate, indented) {
  // Live client surfaced only by a vertical launch: their long-finished
  // new-client onboarding has no meaningful age/health — show a slim header
  // and let the flow sub-rows carry the information.
  if (c.vertical_only) {
    return (
      <React.Fragment key={c.client_id}>
        <TableRow
          hover
          onClick={() => navigate(paths.onboarding.internal(c.client_slug))}
          sx={{ cursor: 'pointer', '& td': { py: 1, px: 1.5, fontSize: 13 } }}
        >
          <TableCell sx={indented ? { pl: 5 } : undefined}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: 13 }}>{c.client_name}</Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
              <Chip label={STATUS_LABELS[c.client_status] || c.client_status} size="small"
                color={STATUS_COLORS[c.client_status] || 'default'} sx={{ fontSize: 10, height: 18 }} />
              <Chip label="Vertical launch" size="small" color="secondary" variant="outlined"
                sx={{ fontSize: 10, height: 18 }} />
            </Stack>
            {renderVerticalsCaption(c)}
          </TableCell>
          <TableCell colSpan={7} />
          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
            <Stack direction="row" spacing={0.5}>
              <Button component={Link} to={paths.onboarding.internal(c.client_slug)}
                size="small" sx={{ fontSize: 11, minWidth: 0, px: 1 }}>Staff</Button>
              <Button component="a" href={`/onboarding/${c.client_slug}`} target="_blank"
                size="small" sx={{ fontSize: 11, minWidth: 0, px: 1 }}>Client</Button>
            </Stack>
          </TableCell>
        </TableRow>
        {renderFlowRows(c, navigate, indented)}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment key={c.client_id}>
    <TableRow
      hover
      onClick={() => navigate(paths.onboarding.internal(c.client_slug))}
      sx={{ cursor: 'pointer', '& td': { py: 1, px: 1.5, fontSize: 13 } }}
    >
      <TableCell sx={indented ? { pl: 5 } : undefined}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: 13 }}>{c.client_name}</Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
          <Chip label={STATUS_LABELS[c.client_status] || c.client_status} size="small"
            color={STATUS_COLORS[c.client_status] || 'default'} sx={{ fontSize: 10, height: 18 }} />
          {HEALTH_LABELS[c.health] && (
            <Chip
              label={`${HEALTH_LABELS[c.health]}${c.stalled_on ? ` — on ${c.stalled_on}` : ''}`}
              size="small" color={HEALTH_COLORS[c.health]} sx={{ fontSize: 10, height: 18, fontWeight: 700 }}
            />
          )}
        </Stack>
        {renderVerticalsCaption(c)}
      </TableCell>
      <TableCell>
        {c.days_in_onboarding != null ? (
          <>
            <Typography variant="body2" fontWeight={700} sx={{
              fontSize: 13,
              color: c.health === 'stalled' ? 'error.main' : c.health === 'at_risk' ? 'warning.main' : 'text.primary',
            }}>
              {c.days_in_onboarding}d
            </Typography>
            {c.idle_days != null && c.idle_days >= 3 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                idle {c.idle_days}d
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="caption" color="text.disabled">—</Typography>
        )}
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <LinearProgress variant="determinate" value={c.progress}
            sx={{ flex: 1, height: 5, borderRadius: 3 }}
            color={c.progress === 100 ? 'success' : 'primary'} />
          <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>{c.progress}%</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          {c.completed_tasks}/{c.total_tasks}
        </Typography>
      </TableCell>
      <TableCell>
        {c.current_phase ? (
          <Chip label={c.current_phase} size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 20, fontWeight: 600 }} />
        ) : (
          <Chip label="Done" size="small" color="success" sx={{ fontSize: 10, height: 20 }} />
        )}
      </TableCell>
      <TableCell>
        {(c.waiting_on_client || []).length > 0 ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {(c.waiting_on_client || []).map((w) => (
              <Chip key={w} label={w} size="small" color="warning" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
            ))}
          </Stack>
        ) : (
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={LD_LABELS[c.lead_delivery_status] || '—'}
          size="small"
          color={LD_COLORS[c.lead_delivery_status] || 'default'}
          variant={c.lead_delivery_status === 'confirmed' ? 'filled' : 'outlined'}
          sx={{ fontSize: 10, height: 18 }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="caption" fontWeight={600} sx={{ color: '#ff680d', fontSize: 11, whiteSpace: 'nowrap' }}>
          {c.estimated_launch ? new Date(c.estimated_launch + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption" sx={{ fontSize: 11, whiteSpace: 'nowrap' }}
          color={c.owner ? 'text.primary' : 'text.disabled'} title={c.owner?.email || 'Unassigned'}>
          {c.owner ? c.owner.name.split(' ')[0] : '—'}
        </Typography>
      </TableCell>
      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
        <Stack direction="row" spacing={0.5}>
          <Button component={Link} to={paths.onboarding.internal(c.client_slug)}
            size="small" sx={{ fontSize: 11, minWidth: 0, px: 1 }}>Staff</Button>
          <Button component="a" href={`/onboarding/${c.client_slug}`} target="_blank"
            size="small" sx={{ fontSize: 11, minWidth: 0, px: 1 }}>Client</Button>
        </Stack>
      </TableCell>
    </TableRow>
    {renderFlowRows(c, navigate, indented)}
    </React.Fragment>
  );
}

export default function OnboardingDashboardPage() {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const currentEmail = authState?.user?.email || '';
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [collapsedParents, setCollapsedParents] = useState({});

  const loadData = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await defaultClient.request({ url: `tasks/onboarding-tasks/dashboard/${params}` });
      setClients(res?.data?.results || []);
      setSummary(res?.data?.summary || null);
    } catch {
      toast.error('Failed to load onboarding dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [statusFilter]);

  const visibleClients = useMemo(() => {
    if (ownerFilter === 'mine') return clients.filter((c) => c.owner?.email === currentEmail);
    if (ownerFilter === 'unassigned') return clients.filter((c) => !c.owner);
    return clients;
  }, [clients, ownerFilter, currentEmail]);

  // Group rows so each parent company appears as a collapsible header followed
  // by its clients. Groups sort by worst health first, then oldest — the
  // dashboard's job is "who needs a push?", so stalled clients surface on top.
  const groupedRows = useMemo(() => {
    const severity = (c) => HEALTH_RANK[c.health] ?? 0;
    const age = (c) => c.days_in_onboarding ?? -1;
    const parents = new Map();
    const standalone = [];
    visibleClients.forEach((c) => {
      if (c.parent_company) {
        const key = c.parent_company.id;
        if (!parents.has(key)) {
          parents.set(key, { parent: c.parent_company, children: [] });
        }
        parents.get(key).children.push(c);
      } else {
        standalone.push(c);
      }
    });
    const groups = [
      ...Array.from(parents.values()).map((g) => ({
        type: 'parent',
        name: g.parent.name || '',
        severity: Math.max(...g.children.map(severity)),
        age: Math.max(...g.children.map(age)),
        ...g,
      })),
      ...standalone.map((c) => ({
        type: 'standalone',
        name: c.client_name || '',
        severity: severity(c),
        age: age(c),
        client: c,
      })),
    ];
    groups.sort(
      (a, b) => b.severity - a.severity || b.age - a.age || a.name.localeCompare(b.name),
    );
    groups.forEach((g) => {
      if (g.children) {
        g.children.sort((a, b) => severity(b) - severity(a) || age(b) - age(a));
      }
    });
    return groups;
  }, [visibleClients]);

  const toggleParent = (parentId) => {
    setCollapsedParents((prev) => ({ ...prev, [parentId]: !prev[parentId] }));
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Onboarding Dashboard</Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            select size="small" label="Owner" value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)} sx={{ minWidth: 130 }}
          >
            <MenuItem value="">Everyone</MenuItem>
            <MenuItem value="mine">Mine</MenuItem>
            <MenuItem value="unassigned">Unassigned</MenuItem>
          </TextField>
          <TextField
            select size="small" label="Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All Active</MenuItem>
            <MenuItem value="new_business">New Business</MenuItem>
            <MenuItem value="onboarding">Onboarding</MenuItem>
          </TextField>
          <Button startIcon={<RefreshIcon />} onClick={loadData} size="small">Refresh</Button>
        </Stack>
      </Stack>

      {/* Process-health KPIs */}
      {summary && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          {[
            { label: 'Stalled', value: summary.stalled, color: summary.stalled > 0 ? 'error.main' : 'text.primary' },
            { label: 'At Risk', value: summary.at_risk, color: summary.at_risk > 0 ? 'warning.main' : 'text.primary' },
            { label: 'Waiting on client 3d+', value: summary.waiting_on_client, color: 'text.primary' },
            {
              label: 'Median days to launch (90d)',
              value: summary.median_days_to_launch_90d != null ? `${summary.median_days_to_launch_90d}d` : '—',
              caption: summary.launches_90d ? `${summary.launches_90d} launch${summary.launches_90d !== 1 ? 'es' : ''}` : 'no launches yet',
              color: 'text.primary',
            },
          ].map((kpi) => (
            <Card key={kpi.label} variant="outlined" sx={{ px: 2, py: 1, minWidth: 150 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {kpi.label}
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ color: kpi.color, lineHeight: 1.2 }}>
                {kpi.value}
              </Typography>
              {kpi.caption && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>{kpi.caption}</Typography>
              )}
            </Card>
          ))}
        </Stack>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={32} /></Box>
          ) : visibleClients.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">
                {ownerFilter ? 'No clients match this owner filter.' : 'No clients in onboarding.'}
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, py: 1, px: 1.5, whiteSpace: 'nowrap' } }}>
                  <TableCell>Client</TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Progress</TableCell>
                  <TableCell>Current Step</TableCell>
                  <TableCell>Waiting On</TableCell>
                  <TableCell>Lead Delivery</TableCell>
                  <TableCell>Launch</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell sx={{ width: 40 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedRows.map((group) => {
                  if (group.type === 'standalone') {
                    return renderClientRow(group.client, navigate, false);
                  }
                  const { parent, children } = group;
                  const collapsed = !!collapsedParents[parent.id];
                  const avgProgress = children.length
                    ? Math.round(children.reduce((s, c) => s + (c.progress || 0), 0) / children.length)
                    : 0;
                  const totalWaiting = children.reduce((s, c) => s + (c.waiting_on_client || []).length, 0);
                  return (
                    <React.Fragment key={`parent-${parent.id}`}>
                      <TableRow
                        hover
                        onClick={() => toggleParent(parent.id)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: 'grey.100',
                          '& td': { py: 0.75, px: 1.5, fontSize: 13, borderBottom: '2px solid', borderColor: 'grey.300' },
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <IconButton size="small" sx={{ p: 0.25 }}>
                              {collapsed ? <KeyboardArrowRightIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                            </IconButton>
                            <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: 13 }}>
                              {parent.name}
                            </Typography>
                            <Chip label={`${children.length} client${children.length !== 1 ? 's' : ''}`}
                              size="small" sx={{ fontSize: 10, height: 18 }} />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {group.age >= 0 && (
                            <Typography variant="body2" fontWeight={700} sx={{
                              fontSize: 13,
                              color: group.severity === 2 ? 'error.main' : group.severity === 1 ? 'warning.main' : 'text.primary',
                            }}>
                              {group.age}d
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <LinearProgress variant="determinate" value={avgProgress}
                              sx={{ flex: 1, height: 5, borderRadius: 3 }}
                              color={avgProgress === 100 ? 'success' : 'primary'} />
                            <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11 }}>{avgProgress}%</Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>avg</Typography>
                        </TableCell>
                        <TableCell />
                        <TableCell>
                          {totalWaiting > 0 ? (
                            <Chip label={`${totalWaiting} pending`} size="small" color="warning" variant="outlined"
                              sx={{ fontSize: 10, height: 18 }} />
                          ) : (
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                          )}
                        </TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell />
                      </TableRow>
                      {!collapsed && children.map((c) => renderClientRow(c, navigate, true))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
